import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { ApiError } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Field, Input } from '@/components/ui/Input';
import { BrandMark } from '@/components/layout/BrandMark';
import { useAuth } from './AuthProvider';

/**
 * Sign-in screen.
 *
 * The server returns one indistinguishable message for a bad password, an
 * unknown account and a disabled account, and this form displays exactly what
 * it was given — it must not add hints that would let an attacker enumerate
 * accounts.
 */
export function LoginPage() {
  const { login } = useAuth();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [retryAfter, setRetryAfter] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setRetryAfter(null);
    setSubmitting(true);
    try {
      await login(identifier, password);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        if (err.status === 429) {
          const match = /(\d+)s/.exec(err.message);
          setRetryAfter(match?.[1] ? Number(match[1]) : null);
        }
      } else {
        setError('Sign-in failed. Please try again.');
      }
      setPassword('');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-full items-center justify-center bg-[var(--color-surface)] px-4">
      <div className="w-full max-w-[22rem]">
        <div className="mb-6">
          <BrandMark className="mb-2.5 h-7 w-7 text-[var(--color-text)]" />
          <h1 className="text-[17px] font-semibold tracking-tight text-[var(--color-text)]">
            Chișinău Insider
          </h1>
          <p className="mt-0.5 text-[12.5px] text-[var(--color-text-muted)]">
            Operations console — authorised access only
          </p>
        </div>

        <form onSubmit={handleSubmit} className="panel space-y-3 p-4">
          <Field label="Email or username">
            <Input
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              autoComplete="username"
              autoFocus
              required
              maxLength={200}
              disabled={submitting}
            />
          </Field>

          <Field label="Password">
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
              maxLength={200}
              disabled={submitting}
            />
          </Field>

          {error ? (
            <div
              role="alert"
              className="rounded-[var(--radius-sm)] border border-transparent bg-[var(--color-negative-soft)] px-2.5 py-2 text-[12.5px] text-[var(--color-negative)]"
            >
              {error}
              {retryAfter ? (
                <span className="mt-0.5 block text-[var(--color-text-muted)]">
                  Repeated failures are throttled to slow down guessing.
                </span>
              ) : null}
            </div>
          ) : null}

          <Button type="submit" variant="primary" className="w-full justify-center" disabled={submitting}>
            {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
            Sign in
          </Button>
        </form>

        <p className="mt-3 text-center text-[11.5px] leading-relaxed text-[var(--color-text-subtle)]">
          Sessions are server-side and expire automatically. No credentials are stored in the browser.
        </p>
      </div>
    </div>
  );
}
